<template>
  <div class="d-flex flex-column" style="height: 100%; background: white">
    <!-- Header -->
    <v-toolbar flat color="white" style="border-bottom: 1px solid #e0e0e0">
      <v-toolbar-title>Group Info</v-toolbar-title>
      <v-spacer />
      <v-btn icon @click="$emit('close')">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-toolbar>

    <!-- Group Details -->
    <div class="flex-grow-1 overflow-y-auto pa-4">
      <!-- Group Image and Name -->
      <div class="text-center mb-4">
        <v-avatar size="100" color="primary">
          <v-img v-if="chat.image_path" :src="mediaUrl + chat.image_path" />
          <span v-else class="white--text text-h4">
            {{ chat.name ? chat.name.charAt(0).toUpperCase() : 'G' }}
          </span>
        </v-avatar>
        <h3 class="mt-3">{{ chat.name || 'Group Chat' }}</h3>
        <p class="text-caption grey--text">{{ memberCount }} members</p>
      </div>

      <v-divider class="my-4" />

      <!-- Member List -->
      <div>
        <div class="d-flex align-center justify-space-between mb-2">
          <h4 class="text-subtitle-1">Members</h4>
          <v-btn
            small
            text
            color="primary"
            @click="showAddMembersDialog = true"
          >
            <v-icon left small>mdi-account-plus</v-icon>
            Add
          </v-btn>
        </div>

        <v-list>
          <v-list-item
            v-for="member in chat.users"
            :key="member.id"
          >
            <v-list-item-avatar>
              <v-badge
                :value="isUserOnline(member.id)"
                color="success"
                dot
                bordered
                overlap
              >
                <v-img v-if="member.image_path" :src="mediaUrl + member.image_path" />
                <v-avatar v-else color="primary">
                  <span class="white--text">{{ member.first_name.charAt(0) }}</span>
                </v-avatar>
              </v-badge>
            </v-list-item-avatar>

            <v-list-item-content>
              <v-list-item-title class="d-flex align-center flex-wrap">
                {{ member.first_name }} {{ member.last_name }}
                <v-chip
                  v-if="member.pivot && member.pivot.role === 'admin'"
                  x-small
                  color="primary"
                  text-color="white"
                  class="ml-2"
                >
                  Admin
                </v-chip>
                <v-chip
                  v-else-if="member.id === chat.created_by_id"
                  x-small
                  color="primary"
                  text-color="white"
                  class="ml-2"
                >
                  Admin
                </v-chip>
              </v-list-item-title>
              <v-list-item-subtitle class="d-flex flex-column" style="line-height: 1.5;">
                <span>{{ member.position ? member.position.description : 'Member' }}</span>
                <span v-if="member.pivot && member.pivot.joined_at" class="text-caption" style="color: #9e9e9e;">
                  <v-icon x-small style="color: #9e9e9e;">mdi-calendar-check</v-icon>
                  Joined {{ formatDate(member.pivot.joined_at) }}
                </span>
              </v-list-item-subtitle>
            </v-list-item-content>

            <v-list-item-action v-if="isCreator && member.id !== chat.created_by_id">
              <v-menu offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon v-bind="attrs" v-on="on">
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item @click="removeMember(member)">
                    <v-list-item-icon>
                      <v-icon color="error">mdi-account-remove</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>Remove</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </div>

      <v-divider class="my-4" />

      <!-- Leave Group Button -->
      <v-btn
        block
        outlined
        color="error"
        @click="showLeaveConfirmation = true"
      >
        <v-icon left>mdi-exit-to-app</v-icon>
        Leave Group
      </v-btn>
    </div>

    <!-- Add Members Dialog -->
    <v-dialog v-model="showAddMembersDialog" max-width="500px" scrollable>
      <v-card>
        <v-card-title>Add Members</v-card-title>
        <v-card-text>
          <v-autocomplete
            v-model="newMembers"
            :items="availableUsers"
            :loading="loadingUsers"
            :search-input.sync="userSearch"
            item-text="first_name"
            item-value="id"
            label="Select Users"
            multiple
            chips
            deletable-chips
            return-object
          >
            <template v-slot:selection="{ item }">
              <v-chip small close @click:close="removeNewMember(item)">
                {{ item.first_name }}
              </v-chip>
            </template>
            <template v-slot:item="{ item }">
              <v-list-item-avatar>
                <v-img v-if="item.image_path" :src="mediaUrl + item.image_path" />
                <v-avatar v-else color="primary">
                  <span class="white--text">{{ item.first_name.charAt(0) }}</span>
                </v-avatar>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>
                  {{ item.first_name }} {{ item.last_name }}
                </v-list-item-title>
              </v-list-item-content>
            </template>
          </v-autocomplete>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="showAddMembersDialog = false">Cancel</v-btn>
          <v-btn
            color="primary"
            :loading="addingMembers"
            :disabled="newMembers.length === 0"
            @click="addMembers"
          >
            Add Members
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Leave Confirmation Dialog -->
    <v-dialog v-model="showLeaveConfirmation" max-width="400px">
      <v-card>
        <v-card-title>Leave Group?</v-card-title>
        <v-card-text>
          Are you sure you want to leave this group? You won't be able to see new messages.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="showLeaveConfirmation = false">Cancel</v-btn>
          <v-btn
            color="error"
            :loading="leavingGroup"
            @click="leaveGroup"
          >
            Leave
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';

export default {
  name: 'GroupInfoPanel',

  props: {
    chat: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      showAddMembersDialog: false,
      showLeaveConfirmation: false,
      newMembers: [],
      userSearch: null,
      users: [],
      loadingUsers: false,
      addingMembers: false,
      leavingGroup: false
    };
  },

  computed: {
    ...mapGetters('chat', ['isUserOnline']),

    memberCount() {
      return this.chat.users?.length || 0;
    },

    isCreator() {
      if (this.chat.created_by_id === this.user.id) return true;
      const me = this.chat.users?.find(u => u.id === this.user.id);
      return me?.pivot?.role === 'admin';
    },

    availableUsers() {
      // Filter out users who are already members
      const memberIds = this.chat.users?.map(u => u.id) || [];
      return this.users.filter(u => !memberIds.includes(u.id) && u.id !== this.user.id);
    }
  },

  watch: {
    showAddMembersDialog(newVal) {
      if (newVal) {
        this.loadUsers();
      }
    },

    userSearch() {
      this.loadUsers();
    }
  },

  methods: {
    async loadUsers() {
      this.loadingUsers = true;
      try {
        const response = await axios.get('/users', {
          params: {
            page: 1,
            rowsPerPage: 50,
            search_keyword: this.userSearch || ''
          }
        });
        this.users = response.data.data;
      } catch (error) {
        console.error('Failed to load users:', error);
        this.$toast.error('Failed to load users');
      } finally {
        this.loadingUsers = false;
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    },

    removeNewMember(user) {
      this.newMembers = this.newMembers.filter(u => u.id !== user.id);
    },

    async addMembers() {
      this.addingMembers = true;
      try {
        await this.$store.dispatch('chat/addGroupMembers', {
          chatId: this.chat.id,
          userIds: this.newMembers.map(u => u.id)
        });

        this.$toast.success('Members added successfully');
        this.showAddMembersDialog = false;
        this.newMembers = [];
      } catch (error) {
        console.error('Failed to add members:', error);
        this.$toast.error('Failed to add members');
      } finally {
        this.addingMembers = false;
      }
    },

    async removeMember(member) {
      const confirmed = await this.$confirm(
        `Remove ${member.first_name} from this group?`,
        {
          title: 'Remove Member',
          color: 'error'
        }
      );

      if (!confirmed) return;

      try {
        await this.$store.dispatch('chat/removeGroupMember', {
          chatId: this.chat.id,
          userId: member.id
        });

        this.$toast.success('Member removed successfully');
      } catch (error) {
        console.error('Failed to remove member:', error);
        this.$toast.error('Failed to remove member');
      }
    },

    async leaveGroup() {
      this.leavingGroup = true;
      try {
        await this.$store.dispatch('chat/leaveGroupChat', this.chat.id);

        this.$toast.success('You left the group');
        this.showLeaveConfirmation = false;
        this.$emit('close');

        // Navigate back to messenger home
        this.$router.push('/messenger');
      } catch (error) {
        console.error('Failed to leave group:', error);
        this.$toast.error('Failed to leave group');
      } finally {
        this.leavingGroup = false;
      }
    }
  }
};
</script>

<style scoped>
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}
</style>
